import * as React from 'react';
import { Card, Loading, Page } from 'sha-el-design';
import { nestedAccess } from 'sha-el-design/lib/helpers';
import { GraphqlQuery } from 'requestapijs';
import { match } from 'react-router';
import { Link } from 'react-router-dom';
import {
  CallResponseNodeConnection,
  CallResponseNode,
} from '../../../../schema';
import { RetailerQnAQuery } from './constants';
import { deep } from '../deep';
import { MdArrowBack, MdHome } from 'react-icons/md';
import '../../Common/listPageStyles';

/**
 * The questionnaire answers recorded for one call.
 *
 * Was a wall of coloured <Tag>s in a 12/12 grid: long free-text answers
 * overflowed their pill, every question/answer pair in the .map shared the
 * React keys 'question' and 'answer', and only edges[0] was ever rendered --
 * a call with more than one response silently showed just the first.
 *
 * The answer text is also parsed defensively now. `answers` entries are JSON
 * strings; a malformed or short entry used to throw straight out of render
 * (JSON.parse with no guard, indexed by the question's position).
 */
export class RetailerQandA extends React.Component<Props> {

  parseAnswer = (raw: string): string => {
    if (!raw) { return ''; }
    try {
      const parsed = JSON.parse(raw);
      const response = parsed && parsed.response;
      if (Array.isArray(response)) {
        // The calling screen seeds a multi-select with an empty first element,
        // so a stored ["", "Yes"] rendered as ", Yes" -- a phantom option the
        // retailer never chose. Drop the blanks here; the root cause is in
        // Call/Questions.tsx and is a separate, riskier change.
        return response.filter((r) => r !== null && r !== undefined && String(r).trim() !== '')
          .join(', ');
      }
      return response === undefined || response === null ? '' : String(response);
    } catch (e) {
      // Keep the raw text rather than blanking the row -- seeing something
      // odd beats seeing nothing, and it never takes the page down.
      return String(raw);
    }
  }

  breadcrumbs = () => ({
    seperator: '/',
    paths: [
      { text: <MdHome />, link: '/dashboard' },
      { text: 'Retailer Report', link: '/dashboard/retailer_report' },
      { text: 'Call Response', link: `/dashboard/retailer_report/${this.props.match.params.id}` },
    ].map((v) => ({
      render: () => (
        <Link key={v.link} to={v.link}>
          {v.text}
        </Link>
      ),
    })),
  })

  response = (node: CallResponseNode, index: number, total: number) => {
    const questions: string[] = (node.question as any) || [];
    const answers: string[] = (node.answers as any) || [];
    const remarks = node.remarks;
    return (
      <div className='cs-qna' key={node.id || index}>
        {total > 1 && (
          <p className='cs-qna__resp-head'>Response {index + 1} of {total}</p>
        )}

        {node.isAlert && (
          <div className='cs-qna__alert' role='status'>
            <b>Submitted with alert.</b>
            <span>The caller flagged this response for attention.</span>
          </div>
        )}

        <div className='cs-qna__meta'>
          <div className='cs-qna__meta-item'>
            <span className='cs-qna__meta-label'>Retailer</span>
            <span className='cs-qna__meta-value'>
              {nestedAccess(node, 'call', 'retailer', 'firm') || '—'}
            </span>
          </div>
          <div className='cs-qna__meta-item'>
            <span className='cs-qna__meta-label'>Caller</span>
            <span className='cs-qna__meta-value'>
              {deep(node, 'workHistory', 'work', 'caller', 'employee', 'name') || '—'}
            </span>
          </div>
          <div className='cs-qna__meta-item'>
            <span className='cs-qna__meta-label'>Call duration</span>
            <span className='cs-qna__meta-value'>
              {nestedAccess(node, 'call', 'duration') || '—'}
            </span>
          </div>
        </div>

        <div className='cs-qna__list'>
          {questions.map((question, i) => {
            const answer = this.parseAnswer(answers[i]);
            return (
              <div className='cs-qna__item' key={`q-${i}`}>
                <p className='cs-qna__q'>{question}</p>
                <p className={`cs-qna__a${answer ? '' : ' cs-qna__a--empty'}`}>
                  {answer || 'No answer recorded'}
                </p>
              </div>
            );
          })}
          {remarks && (
            <div className='cs-qna__item' key='remarks'>
              <p className='cs-qna__q'>REMARKS</p>
              <p className='cs-qna__a'>{remarks}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className='cs-form'>
        <Page
          title={'Call Response'}
          breadcrumbs={this.breadcrumbs()}
          backIcon={
            <Link to={'/dashboard/retailer_report'}>
              <MdArrowBack />
            </Link>
          }
        >
          <div className='cs-form__card'>
            <Card>
              <GraphqlQuery
                queryString={RetailerQnAQuery}
                variables={{
                  callHistoryId: this.props.match.params.id,
                }}
                render={(response: { allCallResponses: CallResponseNodeConnection }, error, loading) => {
                  const edges = nestedAccess(response, 'allCallResponses', 'edges');
                  if (error || (!loading && !edges)) {
                    return (
                      <div className='cs-list__empty'>
                        <h4>Could not load this response</h4>
                        <p>The call may have been removed since the report was generated.</p>
                      </div>
                    );
                  }
                  return (
                    <Loading
                      isLoading={loading}
                      render={() => {
                        if (!edges.length) {
                          return (
                            <div className='cs-list__empty'>
                              <h4>No questionnaire answers</h4>
                              <p>
                                This call has no recorded response. Only answered calls
                                open the questionnaire.
                              </p>
                            </div>
                          );
                        }
                        // Render every response, not just edges[0].
                        return <>{edges.map((edge, i) => this.response(edge.node, i, edges.length))}</>;
                      }}
                    />
                  );
                }}
              />
            </Card>
          </div>
        </Page>
      </div>
    );
  }
}

interface Props {
  match: match<{ id: string }>;
}
